import React from 'react';
import { currentPreviousTooltip } from './currentPreviousTooltip';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  MUTED_PALETTE,
  SEVERITY_DEEP_GREEN,
  SEVERITY_DEEP_RED,
  SeverityBand,
  severityBandStops,
} from './chartPalette';
import { bloodDropLabel, BLOOD_DROP_EDGE_OPTIONS } from './bloodDropLabel';

const bloodDrop = require('../../assets/imgs/blood_drop_black.png');
const bloodDropPrevious = require('../../assets/imgs/blood_drop_blue_50.png');

/**
 * RDW-style severity bar for the MVX four-band markers. Mirrors
 * RDWReferenceRangeChart exactly — a NORMALIZED axis so the bands are evenly
 * proportioned (the big optimal band 62.5%, three 12.5% severity bands), band
 * names centered ABOVE each band, values labeled below — the band boundaries
 * plus evenly spaced gradations across the green band, as RDW labels its own
 * optimal band — and the blood-drop marker mapped piecewise into its zone.
 *
 * Default: green/Optimal on the LEFT (the index scores + GlycA, lower-better).
 * `reversed`: green/Optimal on the RIGHT (small HDL-P, higher-better) — the
 * three severity bands sit on the low/left end instead.
 */
const { green: GREEN, yellow: YELLOW, orange: ORANGE, red: RED } = MUTED_PALETTE;

const GRID_LINE_COLOR = '#CDD7EA';

// Optimal band = 62.5%; the three severity bands 12.5% each.
const NORM_GREEN_LEFT = [0, 166.67, 200, 233.33, 266.67];
const NORM_GREEN_RIGHT = [0, 33.33, 66.67, 100, 266.67];

// Every bar chart in the app ticks on the same eighth-grid — the standard
// reference chart, RDW, eGFR and the symmetric MVX optimal bar all use it — so
// a stack of rows lines up column for column. Both band layouts above put
// their four boundaries ON this grid, so the grid is also the tick list: the
// four positions a layout does not use as a boundary fall INSIDE the big green
// band, which is what gives it interior gradations instead of a bare 62.5% of
// the axis. (Darren Schmidt asked for these, 2026-10-04: a green band running
// 0–260 with only "260" under its right edge is hard to read a drop against.)
const GRID = [0, 33.33, 66.67, 100, 133.33, 166.67, 200, 233.33, 266.67];

/** A labeled tick: where it sits on the normalized axis, and the value it means. */
export interface MvxSeverityTick {
  pos: number;
  value: number;
}

/**
 * The labeled ticks for one bar: the band boundaries, plus evenly spaced
 * gradations across the green band (as RDWReferenceRangeChart labels its own
 * optimal band). The outer padded edges stay unlabeled — the low end of a
 * mirrored bar and the top of every bar are chart padding, not range bounds.
 * Pure, so the arithmetic is unit-testable.
 *
 * The gradations are a RULER, so the printed numbers have to step by a constant
 * amount or the eye reads the scale as broken. Two rules get that:
 *
 *  • At least one decimal, whatever the marker's entry precision. The index
 *    scores are whole numbers on entry, but their bands do not divide evenly —
 *    MVX 0–43 steps by 8.6 — so rounding gradations to whole numbers printed
 *    9, 17, 26, 34: gaps of 9, 8, 9, 8.
 *  • Round the STEP, then accumulate, rather than rounding each gradation off
 *    the true position. Small HDL-P's band steps by 1.122, which rounds
 *    alternately to 1.1 and 1.2 (20.1, 21.2, 22.4, 23.5) when each value is
 *    rounded on its own; rounding the step to 1.1 first gives 20.1, 21.2, 22.3,
 *    23.4. The last gradation then sits a rounding error off its true position,
 *    which is sub-pixel and the right trade: these are reading gradations, not
 *    stored bounds, and the band edges still print their exact values.
 *
 * (Both rules are Darren Schmidt's reading of the bars, 2026-10-04.)
 */
export function severityTicks(
  boundaries: number[],
  reversed?: boolean,
  precision?: number,
  softenFrom?: number,
): MvxSeverityTick[] {
  const N = reversed ? NORM_GREEN_RIGHT : NORM_GREEN_LEFT;
  // Green is the last band when mirrored, the first otherwise.
  const g = reversed ? 3 : 0;
  const [gLo, gHi] = [N[g], N[g + 1]];
  const [vLo, vHi] = [boundaries[g], boundaries[g + 1]];
  const decimals = Math.max(1, precision ?? 0);
  const round = (n: number): number => Math.round(n * 10 ** decimals) / 10 ** decimals;
  const ticks: MvxSeverityTick[] = [];
  // The band boundaries, labelled with the stored numbers. N[0] on a mirrored
  // bar and N[4] on any bar are chart padding, not bounds, so they stay bare.
  N.forEach((pos, edge) => {
    const padded = edge === 4 || (reversed && edge === 0);
    if (!padded) ticks.push({ pos, value: boundaries[edge] });
  });

  const soften =
    softenFrom !== undefined && softenFrom > Math.min(vLo, vHi) && softenFrom < Math.max(vLo, vHi)
      ? softenFrom
      : undefined;

  if (soften === undefined) {
    // No target: the gradations sit on the house eighth-grid and divide the
    // whole green band, which is what every bar did before 2026-10-08.
    const interior = GRID.filter(pos => pos > gLo && pos < gHi && N.indexOf(pos) < 0);
    const step = round((vHi - vLo) / (interior.length + 1));
    interior.forEach((pos, k) => ticks.push({ pos, value: round(vLo + step * (k + 1)) }));
    return ticks.sort((a, b) => a.pos - b.pos);
  }

  // With a target, the ruler runs to the TARGET rather than to the band
  // edge: Dr. Schmidt asked for GlycA's marks "evenly up to 260", not evenly
  // up to 300. The stretch beyond the target is the gradient, and carries
  // no marks of its own.
  //
  // These positions come off the real linear mapping rather than the grid, so
  // a mark saying 132 sits where 132 actually is. That does cost the
  // column-for-column alignment with the other rows, which is the trade: a
  // ruler that lies is worse than a ruler that does not line up.
  const { good, target, vGood } = targetSplit(boundaries, reversed);
  targetGradations(vGood, soften, precision).forEach((value, k) => {
    ticks.push({ pos: good + ((target - good) * (k + 1)) / (GRADATIONS + 1), value });
  });
  ticks.push({ pos: target, value: soften });
  return ticks.sort((a, b) => a.pos - b.pos);
}

/**
 * The interior marks between a bar's floor and its target, as VALUES.
 *
 * Pulled out of severityTicks so the hero gauge can print the same ruler
 * (Maryann, 2026-10-16: "can we put all of the same tick marks that are in
 * the bar graph in the gauge"). The gauge positions them on its own linear
 * arc, where the bar bends its green band around TARGET_FRACTION, so only the
 * numbers are shared. That is the part that has to agree: a mark reading 17
 * on one and 17.2 on the other is the kind of difference nobody can explain.
 *
 * Both rounding rules live here, which is why they can only be got right
 * once: at least one decimal whatever the entry precision, and round the
 * STEP then accumulate rather than rounding each mark off its true position.
 * See severityTicks for what each one is for.
 */
export function targetGradations(from: number, target: number, precision?: number): number[] {
  const decimals = Math.max(1, precision ?? 0);
  const round = (n: number): number => Math.round(n * 10 ** decimals) / 10 ** decimals;
  const step = round((target - from) / (GRADATIONS + 1));
  const out: number[] = [];
  for (let k = 1; k <= GRADATIONS; k++) out.push(round(from + step * k));
  return out;
}

/**
 * Interior marks between the floor and the target. THREE.
 *
 * Four at first, carried over from the eighth-grid, which gave these bars one
 * mark more on the approach to their target than the window markers have on
 * the approach to their mean. Maryann, 2026-10-08: "there should only be three
 * tick marks in between there, then it matches up with all the other graphs."
 */
const GRADATIONS = 3;

/**
 * Where the target sits inside the green band, as a fraction of it.
 *
 * FIXED, not derived from the values, and that is the whole point. The bar is
 * already a normalized one — the green band is 62.5% of the axis whatever
 * range it covers — so letting the target fall wherever the arithmetic put
 * it meant GlycA's 260 landed at the chart centre and MMX's 45 landed at
 * 107.1, and the two bars disagreed about where the target lives.
 *
 * 0.8 of the green band is 133.33 of 266.67, the centre of the chart, which is
 * exactly where MvxOptimalBar draws the mean tick on the window markers. So
 * every bar in the table now marks its target in the same column, whichever
 * of the two chart types drew it. GlycA already landed there, so it does not
 * move.
 */
const TARGET_FRACTION = 0.8;

/**
 * THE SEVERITY RAMP lives in chartPalette (SEVERITY_GREEN_FADE_REACH and the
 * three constants beside it), because the hero gauge shades off the same
 * numbers: it samples the stops along its arc where these bars hand them to
 * Highcharts as a gradient. Two copies would let the gauge and the bar under
 * it paint the same score two different colors.
 */

/**
 * THE LABEL ROW ON A BAR WITH A TARGET (Maryann, 2026-10-09).
 *
 * Every word in it names a NUMBER on the scale rather than a band, because
 * that is what the practitioner is actually asking of this row: what is this
 * tick, and what is that one.
 *
 *   Metabolic Resilience Target (the lower the better)  Borderline  Lab  Alarm
 *   └──────── right-aligned, ending ON the target ────┘      39      62    77
 *
 * The sentence is right-aligned so it finishes at the target tick and runs
 * back across the stretch it describes: the whole green run up to the target
 * IS the metabolic resilience target, and the tick is where it ends. The
 * direction goes in the same sentence because the bar cannot show it.
 *
 * Then the three boundaries, each word centred on its own tick:
 *
 *   BORDERLINE  the end of the optimal band, where the fade finishes. It
 *               names the NUMBER, which is what the Master Ranges sheet calls
 *               that column too. The gauge's Borderline zone opens earlier,
 *               one point past the target, so the word marks the end of the
 *               stretch on the bar and the start of it on the gauge
 *               (Maryann, 2026-10-09, who chose this word over Acceptable).
 *   LAB         the top of the lab's own reference interval.
 *   ALARM       the last value before alarm. Those two are the "Lab Max" and
 *               "Alarm Max" columns of the sheet, so the bar and the sheet
 *               name the same numbers the same way.
 *
 * Nothing names the orange stretch between Lab and Alarm. It is the gap
 * between two labeled ticks, which is what it was all along, and "> Lab" was
 * a band name in a row that no longer has any.
 */
export const TARGET_LABEL = 'Metabolic Resilience Target';

/**
 * What the other lower-better bars call their target.
 *
 * Just "Target". The index is already named two inches to the left, and
 * naming it again over the bar costs a line of type to repeat it. More to
 * the point, naming the SUBJECT inverts on MMX: it measures malnutrition, so
 * "Nourishment Target (the lower the better)" would tell a practitioner that
 * less nourishment is better. With no noun in front of it, "the lower the
 * better" can only attach to the score, which is what is meant on all four
 * (Maryann, 2026-10-15).
 *
 * MVX keeps the full phrase because "Metabolic Resilience Targets" is
 * Dr. Schmidt's own name for the whole set, and MVX heads the panel.
 */
export const SHORT_TARGET_LABEL = 'Target';
const DIRECTION_NOTE = '(the lower the better)';
export const BOUNDARY_LABELS = ['Borderline', 'Lab', 'Alarm'];

/**
 * The mirrored bar keeps the old band names, centred on their bands, and the
 * green one sits BETWEEN the second and third labeled ticks of the band
 * counting from the good end rather than in the middle of it. Small HDL-P
 * shows both ends of the scale, so it needs no direction note and has no
 * target tick for a sentence to end on.
 */
const OPTIMAL_LABEL_TICK = 1;

/**
 * The band names are a LEGEND, not a heading.
 *
 * They were bold #333: heavier than the tick numbers below them and as loud
 * as the marker names beside them, which inverted the hierarchy. What should
 * carry a reader through this panel is the marker, its value and the drop;
 * the band names are read once and then stop being looked at. With five of
 * the nine rows now leading a group, that weight made the whole section
 * read as busy (Maryann, 2026-10-15).
 *
 * Regular weight in a warm gray that still clears AA at this size: 5.36:1 on
 * white, against 3.74:1 for the secondary gray used in the Insights panel,
 * which is too light for 10px type.
 */
const LABEL_COLOR = '#6F6A64';

/** The band-name label treatment, shared by every word in the label row. */
const LABEL_STYLE = { color: LABEL_COLOR, fontSize: '10px', fontWeight: 'normal' };

/**
 * The target's position, and the two value ranges either side of it.
 * Green runs low→high on a normal bar and high→low on a mirrored one, so the
 * "good" end is whichever end the optimal values sit at.
 */
function targetSplit(
  boundaries: number[],
  reversed: boolean | undefined,
): { good: number; target: number; edge: number; vGood: number; vEdge: number } {
  const N = reversed ? NORM_GREEN_RIGHT : NORM_GREEN_LEFT;
  const g = reversed ? 3 : 0;
  const [good, edge] = reversed ? [N[g + 1], N[g]] : [N[g], N[g + 1]];
  const [vGood, vEdge] = reversed
    ? [boundaries[g + 1], boundaries[g]]
    : [boundaries[g], boundaries[g + 1]];
  return { good, target: good + (edge - good) * TARGET_FRACTION, edge, vGood, vEdge };
}

export type MvxSeverityBarProps = {
  /** Five value-space edges, low → high, matching the four segment order. */
  boundaries: number[];
  /**
   * The five numbers to PRINT, when they differ from the geometry. The index
   * bars pass the last in-band score (34, 62, 77) while the band itself still
   * ends at 35, 62.99… and 77.99…; see `indexBoundaryLabels`. Defaults to
   * `boundaries`, which is what every factor bar wants.
   */
  labelBoundaries?: number[];
  /** Band names in left→right order, e.g. ['Optimal','Lab','> Lab','Alarm']. */
  labels?: string[];
  value: number;
  /** The previous test's value; draws the blue comparison drop beside the black one. */
  previousValue?: number;
  /** Collection dates for the hover box, already formatted. */
  currentDate?: string;
  previousDate?: string;
  format: (n: number) => string;
  /** Show the band-name labels above the bar. */
  showLabels?: boolean;
  /** Mirror the bar so green/Optimal is the big band on the high (right) end. */
  reversed?: boolean;
  /** The marker's entry precision; the green-band gradations use at least one decimal. */
  precision?: number;
  /** Overrides TARGET_LABEL for a bar that is not the panel's headline. */
  targetLabel?: string;
  /**
   * A value INSIDE the optimal band where the green starts giving way to
   * yellow, drawn as a gradient rather than a hard edge.
   *
   * Dr. Schmidt, 2026-10-08, for GlycA: optimal now tops out at 300, but 260
   * is the figure he actually wants people working toward, and everything between
   * is progressively less good rather than equally fine. A solid green band to
   * 300 would say the opposite.
   *
   * Only the lower-better orientation is in use. The mirrored one is
   * implemented for symmetry and has no marker behind it yet.
   */
  softenFrom?: number;
};

const reflowSoon = (chart: Highcharts.Chart): void => {
  setTimeout(() => {
    try {
      chart.reflow();
    } catch (e) {
      /* chart unmounted */
    }
  }, 0);
};

export class MvxSeverityBar extends React.Component<MvxSeverityBarProps> {
  private norm(): number[] {
    return this.props.reversed ? NORM_GREEN_RIGHT : NORM_GREEN_LEFT;
  }

  private colors(): string[] {
    return this.props.reversed ? [RED, ORANGE, YELLOW, GREEN] : [GREEN, YELLOW, ORANGE, RED];
  }

  /**
   * The three bands above optimal, as ONE ramp rather than three flat colors.
   *
   * Dr. Schmidt, 2026-10-13, asked where the alarm starts and answered that
   * the question is the wrong shape: "All these numbers are gradiently bad.
   * Shade into red starting at 62", and for IVX, "shade into red and make the
   * red darker starting at 58". 62 and 58 are those two scores' lab limits.
   *
   * EACH COLOR LANDS ON ITS OWN TICK, and holds long enough either side of it
   * to be read as that color (Maryann, 2026-10-14: "there's not enough yellow
   * on both sides of the borderline tick and there's not enough orange to the
   * left of the Lab tick"). A ramp that only passes through a hue on its way
   * somewhere else shows no hue at all.
   *
   *   Borderline tick   yellow, with the green fade finishing before it and
   *                     yellow holding past it into the Lab band
   *   Lab tick          orange, reached across the rest of the Lab band
   *   just past it      red, which is what he asked for, and it holds
   *   Alarm tick        red, deepening to the end of the bar
   *
   * ⚠ DISPLAY ONLY. The zones are unchanged: a score is still Borderline from
   * one past its target, Elevated past the lab limit and High past the alarm,
   * and the gauge still reads off those. He described a color, not a
   * threshold, which is why this is paint and not a new bound. (Where he did
   * mean a threshold he said so: "500 is really bad, that is the first alarm"
   * moved GlycA's alarm.)
   */
  private severityFill(i: number): any {
    const { reversed } = this.props;
    // Band 1, 2 or 3 counted away from the green end, which on a mirrored bar
    // is the other end of the axis.
    const band = severityBandStops((reversed ? 3 - i : i) as SeverityBand);
    // The axis runs low to high left to right on both layouts, so a mirrored
    // bar walks the same ramp from the other end.
    const stops: [number, string][] = reversed
      ? band.map(([at, color]) => [1 - at, color] as [number, string]).reverse()
      : band;
    return { linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 }, stops };
  }

  // Map a value-space number to the normalized axis, piecewise-linear across the
  // four bands so the drop sits proportionally within its zone.
  //
  // The green band BENDS at the target when there is one: the stretch from
  // the floor to the target gets 80% of the band and the stretch from the target to
  // the edge gets the remaining 20%, so the target always lands in the same
  // column (see TARGET_FRACTION). The drop has to follow the same bend, or a
  // value sitting exactly on the target would not sit on its own tick.
  private toNorm(value: number): number {
    const b = this.props.boundaries;
    const N = this.norm();
    const { reversed, softenFrom } = this.props;
    if (value <= b[0]) return N[0];
    if (value >= b[4]) return N[4];

    const g = reversed ? 3 : 0;
    const inGreen = value > b[g] && value < b[g + 1];
    if (softenFrom !== undefined && inGreen) {
      const { good, target, edge, vGood, vEdge } = targetSplit(b, reversed);
      const within = (v0: number, v1: number, p0: number, p1: number) =>
        v1 === v0 ? p0 : p0 + ((value - v0) / (v1 - v0)) * (p1 - p0);
      const towardsEdge = reversed ? value < softenFrom : value > softenFrom;
      return towardsEdge
        ? within(softenFrom, vEdge, target, edge)
        : within(vGood, softenFrom, good, target);
    }

    for (let i = 0; i < 4; i++) {
      if (value <= b[i + 1]) {
        const t = (value - b[i]) / (b[i + 1] - b[i]);
        return N[i] + t * (N[i + 1] - N[i]);
      }
    }
    return N[4];
  }

  render() {
    const {
      boundaries,
      labelBoundaries,
      labels,
      value,
      previousValue,
      currentDate,
      previousDate,
      format,
      showLabels,
      reversed,
      precision,
      softenFrom,
      targetLabel,
    } = this.props;
    const N = this.norm();
    const COLORS = this.colors();
    const normValue = this.toNorm(value);
    // -12 always, the house offset: with the label's 5px padding it centers the
    // 15px drop on its point (-12 + 5 + 7.5 = +0.5px). This USED to flip to +12
    // for points in the lower half, meant as an anti-clipping nudge, which
    // silently moved those drops ~24.5px to the RIGHT of the value they report —
    // visible on Small HDL-P, the one marker whose drop lands down there.
    // Clipping at the axis ends is BLOOD_DROP_EDGE_OPTIONS' job, as in RDW.
    const offset = -12;
    const normPrevious = previousValue === undefined ? undefined : this.toNorm(previousValue);

    // Where the green gives way, as a fraction across the green band, which
    // is what a Highcharts gradient stop takes.
    const g = reversed ? 3 : 0;
    const soften =
      softenFrom === undefined ||
      softenFrom <= Math.min(boundaries[g], boundaries[g + 1]) ||
      softenFrom >= Math.max(boundaries[g], boundaries[g + 1])
        ? // A soften point outside its own band would paint the whole thing
          // one colour; treat it as absent rather than draw something wrong.
          undefined
        : (() => {
            const { target } = targetSplit(boundaries, reversed);
            // The gradient starts where the target tick is, which is a fixed
            // share of the band rather than wherever the value fell.
            return { pos: target, frac: reversed ? 1 - TARGET_FRACTION : TARGET_FRACTION };
          })();

    // Band boundaries + the green band's interior gradations, keyed by axis
    // position for the formatter below.
    const numByPos: { [k: string]: number } = {};
    severityTicks(labelBoundaries ?? boundaries, reversed, precision, softenFrom).forEach(t => {
      numByPos[String(t.pos)] = t.value;
    });

    /**
     * The green band's fill: deepening toward the good end, then fading to
     * yellow past the target. One band with a multi-stop gradient rather than
     * several, so the band structure is unchanged.
     *
     * THE DEEPENING IS THE SAME ARGUMENT AS THE FADE, pointed the other way.
     * On these bars one direction is better the whole way ("get it as low as
     * you can", Dr. Schmidt, whose lowest MVX seen is 13), so a flat green
     * from the floor to the target says a 33 is as good as a 13. It deepens
     * to SEVERITY_DEEP_GREEN at the floor and is the ordinary green by the
     * target, which is where the fade takes over (Maryann, 2026-10-14).
     *
     * The window markers keep a flat green: their ideal is the middle of the
     * band and both directions away from it are worse, so there is no "good
     * end" to deepen toward. They are drawn by MvxOptimalBar.
     */
    const greenFill = (): any => {
      // No target inside the band: the good end is still the far end, so it
      // deepens the whole way across.
      // Band 0. Without a target it is one sweep from deep green at the floor
      // to plain green at the edge; with one, the sweep ends at the target and
      // the rest fades to yellow, finishing short of the band edge so the last
      // slice is yellow rather than only arriving at yellow on the boundary
      // line. (That is the "yellow on both sides of the borderline tick" half;
      // the Lab band's hold is the other half.)
      const band = severityBandStops(0, soften ? soften.frac : undefined);
      const stops: [number, string][] = reversed
        ? band.map(([at, color]) => [1 - at, color] as [number, string]).reverse()
        : band;
      // x1→x2 left to right: the bar is inverted, so the y-axis runs
      // horizontally and low values sit on the left.
      return { linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 }, stops };
    };

    // Grid lines follow the labels. Inside a softened green band the old
    // eighth-grid positions no longer carry numbers, so drawing them would
    // leave unexplained lines between the marks that do.
    const tickPositions = soften
      ? Array.from(
          new Set([
            ...GRID.filter(pos => pos <= N[g] || pos >= N[g + 1]),
            ...Object.keys(numByPos).map(Number),
          ]),
        ).sort((a, b) => a - b)
      : GRID;

    // A hairline band with no color, there only to carry a label over one tick:
    // a plot LINE would double the grid line the tick already draws, and a plot
    // band's label centres on the band, which is what puts the word over the
    // tick rather than over the middle of something.
    // A hairline band with no color, there only to carry a label over one tick:
    // a plot LINE would double the grid line the tick already draws, and a plot
    // band's label is placed against the band, which is what puts the word on
    // the tick rather than in the middle of something. `align: 'right'` ends
    // the text at the tick instead of centring it on it.
    const labelAt = (
      pos: number,
      text: string,
      align: 'center' | 'right' = 'center',
    ): Highcharts.YAxisPlotBandsOptions => ({
      from: pos - 0.01,
      to: pos + 0.01,
      color: 'rgba(255,255,255,0)',
      label: { text, align, verticalAlign: 'top', y: -5, style: LABEL_STYLE },
    });

    // A bar with a target labels its TICKS (see the comment on TARGET_LABEL).
    // Without one there is no sentence to end on, so the mirrored bar keeps
    // the band names centred on their bands.
    const byTick = !!soften && !reversed;

    // The four colored bands. On a by-tick bar none of them carries a name.
    const bands: Highcharts.YAxisPlotBandsOptions[] = COLORS.map((color, i) => ({
      from: N[i],
      to: N[i + 1],
      color: i === g ? greenFill() : this.severityFill(i),
      label:
        showLabels && labels && !byTick && i !== g
          ? {
              text: labels[i] ?? '',
              align: 'center',
              verticalAlign: 'top',
              y: -5,
              style: LABEL_STYLE,
            }
          : undefined,
    }));

    // The green band's own name, on the bars that still have band names: it
    // sits between the second and third labeled ticks counting from the good
    // end rather than in the middle of the band.
    const greenTicks = severityTicks(labelBoundaries ?? boundaries, reversed, precision, softenFrom)
      .map(t => t.pos)
      .filter(pos => pos >= Math.min(N[g], N[g + 1]) && pos <= Math.max(N[g], N[g + 1]))
      .sort((a, b) => (reversed ? b - a : a - b));

    // The label row. One row's worth for the whole table: `showLabels` is set
    // on the first bar of each kind, and the ticks line up down the column.
    const extraLabels: Highcharts.YAxisPlotBandsOptions[] = !showLabels
      ? []
      : byTick
      ? [
          labelAt(soften!.pos, `${targetLabel ?? TARGET_LABEL} ${DIRECTION_NOTE}`, 'right'),
          ...BOUNDARY_LABELS.map((text, i) => labelAt(N[g + 1 + i], text)),
        ]
      : labels && greenTicks.length > OPTIMAL_LABEL_TICK + 1
      ? [
          labelAt(
            (greenTicks[OPTIMAL_LABEL_TICK] + greenTicks[OPTIMAL_LABEL_TICK + 1]) / 2,
            labels[g] ?? '',
          ),
        ]
      : [];

    const options: Highcharts.Options = {
      chart: { type: 'bar', height: 60, marginTop: 20, marginBottom: 20, backgroundColor: 'transparent' },
      title: { text: '' },
      xAxis: [{ labels: { enabled: false }, tickLength: 0 }],
      yAxis: {
        title: { text: '' },
        min: 0,
        max: 266.67,
        tickPositions,
        labels: {
          enabled: true,
          formatter: function () {
            const key = String((this as any).value);
            return key in numByPos ? format(numByPos[key]) : '';
          },
          style: { fontSize: '10px', color: '#555' },
        },
        gridLineWidth: 1,
        gridLineColor: GRID_LINE_COLOR,
        plotBands: [...extraLabels, ...bands],
      },
      // The house hover box, the same one every other reference-range bar in
      // the app shows: Current over Previous, following the pointer, so a
      // practitioner comparing two draws can read both numbers off the drop
      // (Maryann, 2026-10-15). These bars had it disabled, which made them
      // the only charts in the report that answered nothing on hover.
      //
      // The series y value is a NORMALIZED position, not the result, so the
      // formatter reports the real numbers from the props rather than
      // anything on the point. `followPointer` is what makes it track the
      // drop image, which is a data label rather than a marker.
      tooltip: {
        followPointer: true,
        followTouchMove: true,
        padding: 5,
        useHTML: true,
        formatter: function () {
          const current = format(value);
          const previous = previousValue === undefined ? 'N/A' : format(previousValue);
          return currentPreviousTooltip(current, previous, currentDate, previousDate);
        },
      },
      // Overlay the two series on ONE row so the previous drop sits at the same
      // height as the current one, exactly as RDWReferenceRangeChart does.
      plotOptions: { bar: { grouping: false, pointPlacement: 0 } },
      series: [
        {
          type: 'bar',
          color: 'rgba(255,255,255,0)',
          borderWidth: 0,
          pointWidth: 10,
          data: [normValue],
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            useHTML: true,
            verticalAlign: 'middle',
            x: offset,
            ...BLOOD_DROP_EDGE_OPTIONS,
            format: bloodDropLabel(bloodDrop),
          },
        } as Highcharts.SeriesBarOptions,
        ...(normPrevious === undefined
          ? []
          : [
              {
                type: 'bar',
                color: 'rgba(255,255,255,0)',
                borderWidth: 0,
                pointWidth: 10,
                data: [normPrevious],
                dataLabels: {
                  enabled: true,
                  allowOverlap: true,
                  useHTML: true,
                  verticalAlign: 'middle',
                  x: offset,
                  ...BLOOD_DROP_EDGE_OPTIONS,
                  format: bloodDropLabel(bloodDropPrevious),
                },
              } as Highcharts.SeriesBarOptions,
            ]),
      ],
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    return (
      <HighchartsReact
        options={options}
        highcharts={Highcharts}
        callback={reflowSoon}
        containerProps={{ style: { width: '100%', overflow: 'hidden' } }}
      />
    );
  }
}

export default MvxSeverityBar;
